import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Divider,
  Chip,
  Button,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Rating,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  IconButton,
  Alert,
  DialogContentText,
  Badge
} from '@mui/material';
import {
  Edit as EditIcon,
  CheckCircle as CompletedIcon,
  LocalTaxi as TaxiIcon,
  DirectionsCar as CarIcon,
  Person as DriverIcon,
  PersonPin as CustomerIcon,
  Schedule as ScheduledIcon,
  Cancel as CancelledIcon,
  AccessTime as RequestedIcon,
  DirectionsRun as InProgressIcon,
  Star as StarIcon,
  LocationOn as LocationIcon,
  Room as DestinationIcon,
  Payments as PaymentIcon,
  Save as SaveIcon,
  PlayArrow as StartIcon,
  Stop as EndIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Event as EventIcon,
  FlashOn as FlashOnIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import { 
  fetchRideById, 
  updateRideStatus, 
  addRideFeedback,
  cancelRide,
  deleteRide
} from '../../redux/slices/ridesSlice';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import MapView from '../common/MapView';

// Styled Components
const DetailCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[6],
  },
}));

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  let color;
  let icon;
  
  switch (status) {
    case 'completed':
      color = 'success';
      icon = <CompletedIcon />;
      break;
    case 'in_progress':
      color = 'primary';
      icon = <InProgressIcon />;
      break;
    case 'accepted':
    case 'pre_accepted': // pre_accepted ähnlich wie accepted
      color = 'info';
      icon = <DriverIcon />;
      break;
    case 'open':
      color = 'warning';
      icon = <RequestedIcon />;
      break;
    case 'arrived':
      color = 'success';
      icon = <DriverIcon />;
      break;
    case 'cancelled':
      color = 'error';
      icon = <CancelledIcon />;
      break;
    case 'cancelled_by_driver':
      color = 'error';
      icon = <CancelledIcon />;
      break;
    case 'scheduled':
      color = 'secondary';
      icon = <ScheduledIcon />;
      break;
    default:
      color = 'default';
      icon = <RequestedIcon />;
      break;
  }
  
  return {
    color: color === 'default' ? 'inherit' : 'white',
    backgroundColor: color === 'default' ? undefined : theme.palette[color].main,
    '& .MuiSvgIcon-root': {
      fontSize: '1rem',
    },
  };
});

const LocationBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
  '& .MuiSvgIcon-root': {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
}));

const RideDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const { currentRide, isLoading, error } = useSelector(state => state.rides);
  
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  
  useEffect(() => {
    if (id) {
      dispatch(fetchRideById(id));
    }
  }, [dispatch, id]);
  
  const handleFeedbackDialogOpen = () => {
    setRating(currentRide.rating || 0);
    setFeedback(currentRide.feedback || '');
    setFeedbackDialogOpen(true);
  };
  
  const handleFeedbackDialogClose = () => {
    setFeedbackDialogOpen(false);
  };
  
  const handleFeedbackSubmit = () => {
    dispatch(addRideFeedback({
      rideId: id,
      rating,
      feedback
    }));
    setFeedbackDialogOpen(false);
  };
  
  const handleStatusChange = (newStatus) => {
    dispatch(updateRideStatus({
      rideId: id,
      status: newStatus
    }));
  };

  const handleReleaseDialogOpen = () => {
    setReleaseDialogOpen(true);
  };

  const handleReleaseDialogClose = () => {
    setReleaseDialogOpen(false);
  };
  
  const handleReleaseRide = () => {
    handleReleaseDialogOpen();
  };
  
  const handleReleaseConfirm = () => {
    // Setze den Status zurück auf "scheduled"
    // und entferne die Fahrer- und Fahrzeugdetails
    dispatch(updateRideStatus({
      rideId: id,
      status: 'scheduled',
      additionalData: {
        driverId: null,
        driverName: null,
        driverProfilePic: null,
        driverRating: null,
        driverRatingCount: null,
        driverModel: null,
        driverLicensePlate: null,
        acceptedAt: null // Entferne den Akzeptierungszeitstempel
      }
    }));
    setReleaseDialogOpen(false);
  };
  
  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteRide(id)).unwrap();
      setDeleteDialogOpen(false);
      navigate('/fahrten');
    } catch (error) {
      console.error('Fehler beim Löschen der Fahrt:', error);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };
  
  const formatDate = (dateInput) => {
    if (!dateInput) return 'Nicht angegeben';

    try {
      let date;

      // Firebase Timestamp mit .toDate() Methode
      if (dateInput.toDate) {
        date = dateInput.toDate();
      } else if (dateInput.seconds) {
        // Firebase Timestamp als Objekt mit .seconds Feld
        date = new Date(dateInput.seconds * 1000);
      } else {
        // String oder andere Formate
        date = new Date(dateInput);
      }

      // Prüfe, ob das Datum gültig ist
      if (isNaN(date.getTime())) {
        console.warn('Ungültiges Datum erhalten:', dateInput);
        return 'Nicht angegeben';
      }

      return new Intl.DateTimeFormat('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (error) {
      console.error('Fehler beim Formatieren des Datums:', error, dateInput);
      return 'Nicht angegeben';
    }
  };
  
  const formatCurrency = (value) => {
    if (!value && value !== 0) return 'Nicht angegeben';
    return `${parseFloat(value).toFixed(2)} €`;
  };
  
  const formatDistance = (value) => {
    if (!value && value !== 0) return 'Nicht angegeben';
    return `${parseFloat(value).toFixed(1)} km`;
  };
  
  const formatDuration = (value) => {
    if (!value && value !== 0) return 'Nicht angegeben';
    
    const hours = Math.floor(value / 60);
    const minutes = value % 60;
    
    if (hours > 0) {
      return `${hours} Std. ${minutes} Min.`;
    } else {
      return `${minutes} Min.`;
    }
  };
  
  const translateStatus = (status) => {
    const statusMap = {
      'accepted': 'Akzeptiert',
      'pre_accepted': 'Vom Fahrer angenommen',
      'in_progress': 'Zielfahrt',
      'arrived': 'Angekommen',
      'completed': 'Abgeschlossen',
      'cancelled': 'Storniert',
      'cancelled_by_driver': 'Vom Fahrer storniert',
      'scheduled': 'Geplant',
      'open': 'Offen',
      'online': 'Online',
      'offline': 'Offline'
    };
    
    return statusMap[status] || status;
  };
  
  const getActiveStep = () => {
    const statusSteps = {
      'open': 0,
      'accepted': 1,
      'arrived': 1, // arrived wird wie accepted behandelt (Stufe 1)
      'in_progress': 2,
      'completed': 3,
      'cancelled': -1
    };
    
    return statusSteps[currentRide?.status] || 0;
  };
  
  const canStartRide = () => {
    return currentRide && (currentRide.status === 'accepted' || currentRide.status === 'arrived');
  };
  
  const canCompleteRide = () => {
    return currentRide && (currentRide.status === 'in_progress' || currentRide.status === 'arrived');
  };
  
  const canCancelRide = () => {
    return currentRide && ['accepted', 'scheduled', 'open', 'arrived'].includes(currentRide.status);
  };
  
  const canAddFeedback = () => {
    return currentRide && currentRide.status === 'completed';
  };
  
  if (isLoading) {
    return <Loading message="Fahrt wird geladen..." />;
  }
  
  if (error) {
    return (
      <ErrorMessage
        title="Fehler beim Laden der Fahrt"
        message={error}
        onRetry={() => dispatch(fetchRideById(id))}
      />
    );
  }
  
  if (!currentRide) {
    return (
      <ErrorMessage
        title="Fahrt nicht gefunden"
        message="Die angeforderte Fahrt konnte nicht gefunden werden."
        onRetry={() => navigate('/fahrten')}
      />
    );
  }
  
  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title="Fahrtdetails"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Fahrten', path: '/fahrten' },
          { label: `Fahrt ${id.substring(0, 8)}...`, path: `/fahrten/${id}` }
        ]}
        actionButton={{
          label: 'Bearbeiten',
          icon: <EditIcon />,
          path: `/fahrten/${id}/bearbeiten`
        }}
      />
      
      {/* Fahrt Übersicht */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" component="h1" sx={{ mr: 2 }}>
                  Fahrt #{id.substring(0, 8)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StatusChip 
                    label={translateStatus(currentRide.status)}
                    status={currentRide.status}
                    size="medium"
                    icon={
                      currentRide.status === 'completed' ? <CompletedIcon /> : 
                      currentRide.status === 'in_progress' ? <InProgressIcon /> : 
                      currentRide.status === 'accepted' ? <DriverIcon /> :
                      currentRide.status === 'pre_accepted' ? <DriverIcon /> :
                      currentRide.status === 'arrived' ? <DriverIcon /> :
                      currentRide.status === 'cancelled' ? <CancelledIcon /> :
                      currentRide.status === 'cancelled_by_driver' ? <CancelledIcon /> :
                      <ScheduledIcon />
                    }
                  />
                  <StyledBadge
                    badgeContent={currentRide.convertedToOpenRide ? "!" : 0}
                    color="error"
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                  >
                    <Chip 
                      icon={currentRide.orderType === 1 ? <EventIcon /> : <FlashOnIcon />}
                      label={currentRide.orderType === 1 ? "Vorbestellung" : "Sofortbestellung"}
                      color={currentRide.orderType === 1 ? "secondary" : "primary"}
                      variant="outlined"
                    />
                  </StyledBadge>
                </Box>
              </Box>
              
              {currentRide.convertedToOpenRide && (
                <Alert 
                  severity="warning" 
                  icon={<WarningIcon />}
                  sx={{ mb: 2 }}
                >
                  Diese Vorbestellung wurde am {formatDate(currentRide.convertedAt)} automatisch in eine Sofortbestellung umgewandelt, 
                  da kein Fahrer die Fahrt angenommen hat.
                </Alert>
              )}
              
              <LocationBox>
                <LocationIcon />
                <Typography variant="body1">
                  <strong>Abholadresse:</strong> {currentRide.start}
                </Typography>
              </LocationBox>
              
              <LocationBox>
                <DestinationIcon />
                <Typography variant="body1">
                  <strong>Zieladresse:</strong> {currentRide.destination || 'Kein Ziel angegeben'}
                </Typography>
              </LocationBox>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  <strong>Bestelltyp:</strong> {currentRide.orderType === 1 ? 'Vorbestellung' : 'Sofortbestellung'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Fahrzeugtyp:</strong> {currentRide.vehicleType === 1 ? 'PKW (5 Sitzplätze)' :
                                               currentRide.vehicleType === 2 ? 'Kombi (7 Sitzplätze)' :
                                               currentRide.vehicleType === 3 ? 'Bus (9 Sitzplätze)' : 'Nicht angegeben'}
                </Typography>
                {(currentRide.isKrankenfahrt || currentRide.isRechnungsfahrt || currentRide.isAnschlusstour) && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5, mb: 0.5 }}>
                    {currentRide.isKrankenfahrt && (
                      <Chip label="Krankenfahrt" size="small" color="error" variant="outlined" />
                    )}
                    {currentRide.isRechnungsfahrt && (
                      <Chip label="Rechnungsfahrt" size="small" color="info" variant="outlined" />
                    )}
                    {currentRide.isAnschlusstour && (
                      <Chip label="Anschlusstour" size="small" color="success" variant="outlined" />
                    )}
                  </Box>
                )}
                {currentRide.notes && (
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <strong>Notizen:</strong> {currentRide.notes}
                  </Typography>
                )}
                <Typography variant="body2" color="textSecondary">
                  <strong>Anfragezeitpunkt:</strong> {formatDate(currentRide.timestamp || currentRide.request_time)}
                </Typography>
                {currentRide.scheduledTime && (
                  <Typography variant="body2" color="textSecondary">
                    <strong>Geplant für:</strong> {formatDate(currentRide.scheduledTime)}
                  </Typography>
                )}
                {(currentRide.acceptedTimestamp || currentRide.acceptedAt) && (
                  <Typography variant="body2" color="textSecondary">
                    <strong>Angenommen am:</strong> {formatDate(currentRide.acceptedTimestamp || currentRide.acceptedAt)}
                  </Typography>
                )}
                {currentRide.startedAt && (
                  <Typography variant="body2" color="textSecondary">
                    <strong>Fahrt begonnen:</strong> {formatDate(currentRide.startedAt)}
                  </Typography>
                )}
                {currentRide.arrivedTimestamp && (
                  <Typography variant="body2" color="textSecondary">
                    <strong>Ankunft am Abholort:</strong> {formatDate(currentRide.arrivedTimestamp)}
                  </Typography>
                )}
                {currentRide.passengerPickedUpTimestamp && (
                  <Typography variant="body2" color="textSecondary">
                    <strong>Passagier abgeholt:</strong> {formatDate(currentRide.passengerPickedUpTimestamp)}
                  </Typography>
                )}
                {(currentRide.completedTimestamp || currentRide.completionTimestamp) && (
                  <Typography variant="body2" color="textSecondary">
                    <strong>Abschlusszeitpunkt:</strong> {formatDate(currentRide.completedTimestamp || currentRide.completionTimestamp)}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
          
          <Grid size={{ xs: 12, md: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Zahlungsinformationen
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body1">
                  <strong>Fahrpreis ca.:</strong> {formatCurrency(currentRide.estimatedEarningEur || currentRide.fare)}
                </Typography>
                <Typography variant="body1">
                  <strong>Zahlungsmethoden:</strong> {
                    currentRide.paymentMethods === 1 ? 'Nur Barzahlung' :
                    currentRide.paymentMethods === 2 ? 'Bar- und Kartenzahlung' :
                    currentRide.paymentMethods === 3 ? 'Bar-, Karten- und PayPal-Zahlung' :
                    'Nicht angegeben'
                  }
                </Typography>
              </Box>
              
              {currentRide.rating && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Bewertung:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Rating 
                      value={currentRide.rating} 
                      readOnly 
                      precision={0.5}
                      icon={<StarIcon fontSize="inherit" />}
                    />
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      ({currentRide.rating})
                    </Typography>
                  </Box>
                  {currentRide.feedback && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      "{currentRide.feedback}"
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Grid>
          
          <Grid size={{ xs: 12, md: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {canStartRide() && (
                  <Button 
                    variant="contained" 
                    color="primary"
                    startIcon={<StartIcon />}
                    onClick={() => handleStatusChange('in_progress')}
                  >
                    Fahrt starten
                  </Button>
                )}
                
                {canCompleteRide() && (
                  <Button 
                    variant="contained" 
                    color="success"
                    startIcon={<EndIcon />}
                    onClick={() => handleStatusChange('completed')}
                  >
                    Fahrt abschließen
                  </Button>
                )}
                
                {canCancelRide() && (
                  <Button 
                    variant="outlined" 
                    color="error"
                    startIcon={<CancelledIcon />}
                    onClick={() => handleStatusChange('cancelled')}
                  >
                    Stornieren
                  </Button>
                )}
                
                {/* Neuer Button: Fahrt zurücknehmen für Vorbestellungen mit Status pre_accepted */}
                {currentRide.orderType === 1 && currentRide.status === 'pre_accepted' && (
                  <Button 
                    variant="outlined" 
                    color="warning"
                    startIcon={<CancelledIcon />}
                    onClick={() => handleReleaseRide()}
                  >
                    Fahrt zurücknehmen
                  </Button>
                )}
                
                {canAddFeedback() && (
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<StarIcon />}
                    onClick={handleFeedbackDialogOpen}
                  >
                    {currentRide.rating ? 'Bewertung bearbeiten' : 'Bewertung hinzufügen'}
                  </Button>
                )}

                {['completed', 'cancelled', 'cancelled_by_driver'].includes(currentRide.status) && (
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => navigate('/fahrten/neu', { state: { duplicateRide: currentRide } })}
                  >
                    Erneut erstellen
                  </Button>
                )}
              </Box>
              
              
              {currentRide.status === 'scheduled' && (
                <Button 
                  variant="contained" 
                  component={Link}
                  to={`/fahrten/${id}/zuweisen`}
                  startIcon={<TaxiIcon />}
                >
                  Fahrer zuweisen
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {/* KPIs/Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DetailCard elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CustomerIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Kunde</Typography>
              </Box>
              <Typography variant="body1">{currentRide.customer?.name || currentRide.customerName || 'Nicht angegeben'}</Typography>
              <Typography variant="body2">{currentRide.customer?.email || 'Keine E-Mail'}</Typography>
              <Typography variant="body2">{currentRide.customer?.phone || currentRide.customerPhone || 'Keine Telefonnummer'}</Typography>
            </CardContent>
          </DetailCard>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DetailCard elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DriverIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Fahrer</Typography>
              </Box>
              {currentRide.driver ? (
                <>
                  <Typography variant="body1">{currentRide.driver.name || currentRide.driverName || 'Unbekannt'}</Typography>
                  <Typography variant="body2">
                    Status: {translateStatus(currentRide.driver.isOnline ? 'online' : 'offline')}
                  </Typography>
                  <Typography variant="body2">
                    Tel.: {currentRide.driver.phone || 'Nicht angegeben'}
                  </Typography>
                </>
              ) : (
                <Typography variant="body1">Kein Fahrer zugewiesen</Typography>
              )}
            </CardContent>
          </DetailCard>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DetailCard elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CarIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Fahrzeug</Typography>
              </Box>
              {(currentRide.vehicle || currentRide.driverModel) ? (
                <>
                  <Typography variant="body1">{currentRide.vehicle?.model || currentRide.driverModel || 'Unbekannt'}</Typography>
                  <Typography variant="body2">
                    Kennzeichen: {currentRide.vehicle?.licensePlate || currentRide.driverLicensePlate || 'Nicht angegeben'}
                  </Typography>
                  <Typography variant="body2">
                    Fahrzeugtyp: {currentRide.vehicleType === 1 ? 'PKW (4 Sitzplätze)' : 
                                  currentRide.vehicleType === 2 ? 'Kombi (6 Sitzplätze)' : 
                                  currentRide.vehicleType === 3 ? 'Bus (8 Sitzplätze)' : 'Unbekannt'}
                  </Typography>
                  {currentRide.vehicle?.id && (
                    <Box sx={{ mt: 1 }}>
                      <Button 
                        variant="outlined" 
                        size="small"
                        component={Link}
                        to={`/fahrzeuge/${currentRide.vehicle.id}`}
                      >
                        Fahrzeug anzeigen
                      </Button>
                    </Box>
                  )}
                </>
              ) : (
                <Typography variant="body1">Kein Fahrzeug zugewiesen</Typography>
              )}
            </CardContent>
          </DetailCard>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DetailCard elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PaymentIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Details</Typography>
              </Box>
              <Typography variant="body1">
                <strong>Entfernung:</strong> {formatDistance(currentRide.distanceKm)}
              </Typography>
              <Typography variant="body1">
                <strong>Dauer:</strong> {formatDuration(currentRide.duration || currentRide.estimatedTimeMinutes)}
              </Typography>
              <Typography variant="body1">
                <strong>Fahrpreis ca.:</strong> {formatCurrency(currentRide.estimatedEarningEur || currentRide.fare)}
              </Typography>
            </CardContent>
          </DetailCard>
        </Grid>
      </Grid>
      
      {/* Kartenansicht */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Strecke
        </Typography>
        <Box>
          <MapView
            pickupLocation={
              currentRide.startLatitude && currentRide.startLongitude
                ? { lat: currentRide.startLatitude, lng: currentRide.startLongitude }
                : null
            }
            destinationLocation={
              currentRide.destinationLatitude && currentRide.destinationLongitude && currentRide.destination
                ? { lat: currentRide.destinationLatitude, lng: currentRide.destinationLongitude }
                : null
            }
            pickupAddress={currentRide.start}
            destinationAddress={currentRide.destination}
            currentLocation={currentRide.driver?.location}
            height={400}
            showDirections={true}
            showInfoWindows={true}
          />
        </Box>
      </Paper>
      
      {/* Fahrt-Verlauf */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Fahrtverlauf
        </Typography>
        <Box sx={{ maxWidth: 600 }}>
          <Stepper activeStep={getActiveStep()} orientation="vertical">
            <Step>
              <StepLabel>Fahrt angefragt</StepLabel>
              <StepContent>
                <Typography>
                  Fahrt wurde am {formatDate(currentRide.timestamp)} angefragt.
                  {currentRide.scheduledTime && ` Geplant für ${formatDate(currentRide.scheduledTime)}.`}
                </Typography>
              </StepContent>
            </Step>
            <Step>
              <StepLabel>Fahrer zugewiesen</StepLabel>
              <StepContent>
                {currentRide.driver ? (
                  <Typography>
                    Fahrer {currentRide.driver.name || currentRide.driverName || 'Unbekannt'} wurde am {formatDate(currentRide.acceptedAt || currentRide.acceptedTimestamp)} zugewiesen.
                  </Typography>
                ) : (
                  <Typography>
                    Warten auf Fahrerzuweisung...
                  </Typography>
                )}
              </StepContent>
            </Step>
            <Step>
              <StepLabel>Fahrt in Bearbeitung</StepLabel>
              <StepContent>
                {currentRide.passengerPickedUpTimestamp || currentRide.startedAt ? (
                  <Typography>
                    Fahrt wurde am {formatDate(currentRide.passengerPickedUpTimestamp || currentRide.startedAt)} gestartet.
                  </Typography>
                ) : (
                  <Typography>
                    Warten auf Abholung...
                  </Typography>
                )}
              </StepContent>
            </Step>
            <Step>
              <StepLabel>Fahrt abgeschlossen</StepLabel>
              <StepContent>
                {(currentRide.completedTimestamp || currentRide.completionTimestamp) ? (
                  <Typography>
                    Fahrt wurde am {formatDate(currentRide.completedTimestamp || currentRide.completionTimestamp)} abgeschlossen.
                    {currentRide.rating && ` Mit ${currentRide.rating} Sternen bewertet.`}
                  </Typography>
                ) : (
                  <Typography>
                    Warten auf Abschluss...
                  </Typography>
                )}
              </StepContent>
            </Step>
          </Stepper>
          
          {(currentRide.status === 'cancelled' || currentRide.status === 'cancelled_by_driver') && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Diese Fahrt wurde am {formatDate(currentRide.cancelledTimestamp || currentRide.timestamp)} storniert.
            </Alert>
          )}
        </Box>
      </Paper>
      
      {/* Bewertung Dialog */}
      <Dialog open={feedbackDialogOpen} onClose={handleFeedbackDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Bewertung {currentRide.rating ? 'bearbeiten' : 'hinzufügen'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Wie würden Sie diese Fahrt bewerten?
              </Typography>
              <Rating 
                value={rating}
                onChange={(event, newValue) => {
                  setRating(newValue);
                }}
                precision={0.5}
                size="large"
              />
            </Box>
            <TextField
              fullWidth
              margin="normal"
              label="Feedback"
              multiline
              rows={4}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFeedbackDialogClose}>Abbrechen</Button>
          <Button 
            onClick={handleFeedbackSubmit} 
            variant="contained" 
            color="primary"
            startIcon={<SaveIcon />}
            disabled={!rating}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialog zum Bestätigen der Fahrtfreigabe */}
      <Dialog open={releaseDialogOpen} onClose={handleReleaseDialogClose} maxWidth="sm">
        <DialogTitle>Fahrt zurücknehmen</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body1" gutterBottom>
              Möchten Sie die Vorbestellung wirklich zurücknehmen?
            </Typography>
            <Typography variant="body2" color="error">
              Hinweis: Der Fahrer {currentRide?.driverName} wird dadurch von dieser Fahrt abgemeldet.
              Der Status wird wieder auf "Geplant" gesetzt.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReleaseDialogClose}>Abbrechen</Button>
          <Button 
            onClick={handleReleaseConfirm} 
            variant="contained" 
            color="warning"
          >
            Fahrt zurücknehmen
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Aktionsbuttons */}
      <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={handleDeleteClick}
        >
          Fahrt löschen
        </Button>
        {/* ... other buttons ... */}
      </Box>

      {/* Lösch-Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Fahrt löschen
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Sind Sie sicher, dass Sie diese Fahrt endgültig löschen möchten? 
            Diese Aktion kann nicht rückgängig gemacht werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Abbrechen
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RideDetails;